import { Card, CardContent } from '../ui/card';
import { CheckCircle2 } from 'lucide-react';

/**
 * Shared answer-option metadata used by both authenticated and guest assessments.
 */
const ANSWER_OPTIONS_RICH = [
  { value: 'yes', label: '✅ We Do This', tooltip: 'We have this covered.', bgActive: 'bg-[#10B981]/10', textActive: 'text-[#10B981]', borderActive: 'border-[#10B981]' },
  { value: 'partial', label: '⚠️ We Partially Do This', tooltip: "We've started but we don't do this all the time.", bgActive: 'bg-[#F59E0B]/10', textActive: 'text-[#F59E0B]', borderActive: 'border-[#F59E0B]' },
  { value: 'no', label: "❌ We Don't Do This", tooltip: "We don't or have not done this at all.", bgActive: 'bg-[#EF4444]/10', textActive: 'text-[#EF4444]', borderActive: 'border-[#EF4444]' },
  { value: 'na', label: '❓ Not Sure', tooltip: "We're not sure.", bgActive: 'bg-slate-100', textActive: 'text-[#64748B]', borderActive: 'border-slate-400' },
];

const ANSWER_OPTIONS_SIMPLE = [
  { value: 'yes', label: 'We Do This', bgActive: 'bg-[#10B981]/10', textActive: 'text-[#10B981]', borderActive: 'border-[#10B981]' },
  { value: 'partial', label: 'We Partially Do This', bgActive: 'bg-[#F59E0B]/10', textActive: 'text-[#F59E0B]', borderActive: 'border-[#F59E0B]' },
  { value: 'no', label: "We Don't Do This", bgActive: 'bg-[#EF4444]/10', textActive: 'text-[#EF4444]', borderActive: 'border-[#EF4444]' },
  { value: 'na', label: 'Not Applicable', bgActive: 'bg-slate-100', textActive: 'text-slate-600', borderActive: 'border-slate-400' },
];

export const QuestionCard = ({ question, index, value, onChange, variant = 'rich' }) => {
  const options = variant === 'simple' ? ANSWER_OPTIONS_SIMPLE : ANSWER_OPTIONS_RICH;
  const isAnswered = Boolean(value);

  return (
    <Card
      className={`border-slate-200 shadow-sm transition-all ${
        isAnswered ? 'border-[#10B981]/50' : ''
      }`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isAnswered ? 'bg-[#10B981] text-white' : 'bg-slate-100 text-[#64748B]'
            }`}
          >
            {isAnswered ? (
              <CheckCircle2 className="h-5 w-5" />
            ) : (
              <span className="text-sm font-semibold">{index + 1}</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-[#0F172A] font-medium mb-2">{question.question}</p>
            {question.help_text && (
              <p className="text-sm text-[#64748B] mb-4 bg-slate-50 p-3 rounded-lg border-l-2 border-[#3B82F6]">
                💡 {question.help_text}
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {options.map((option) => {
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    title={option.tooltip}
                    data-testid={`answer-${question.id}-${option.value}`}
                    onClick={() => onChange(question.id, option.value)}
                    className={`flex items-center justify-center px-3 py-3 border-2 rounded-lg cursor-pointer transition-all font-medium text-sm
                      ${isSelected
                        ? `${option.bgActive} ${option.textActive} ${option.borderActive}`
                        : 'border-slate-200 text-[#64748B] hover:bg-slate-50 hover:border-slate-300'
                      }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
